import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMember } from '../Hooks/MemberContext';
import { formatMoney } from '../utils/billingHelpers';
import { accountAmountClasses, accountState } from './accountState';
import { useAccount } from './useAccount';

/**
 * The balance, on the dashboard.
 *
 * Signing in and not seeing what you owe is what made the portal read
 * as a noticeboard rather than an account. This is the compact form of
 * {@link BalanceHero} — same derivation, same traps handled, one line —
 * and it links through to the full account.
 *
 * It renders nothing while loading and nothing on failure: the
 * dashboard's other sections are independently useful, and a broken
 * balance card at the top of the page is worse than no card. The real
 * error, with wording, is on `/account`.
 */
const AccountBalanceCard: React.FC = () => {
  const { selectedHome } = useMember();
  const { balance, loading, error } = useAccount(selectedHome?.home_id);

  if (loading || error || !balance) return null;

  const state = accountState(balance);

  return (
    <Link
      to="/account"
      className="flex items-center justify-between gap-4 rounded-2xl border border-zinc-200 bg-white p-5 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-gray-800 dark:hover:bg-gray-700/50"
    >
      <div className="min-w-0">
        <div className="text-sm text-zinc-500 dark:text-zinc-400">
          {state.owes ? 'Balance due' : 'Your balance'}
        </div>
        <div
          className={`mt-0.5 text-2xl font-semibold tabular-nums ${accountAmountClasses(
            state.status
          )}`}
        >
          {formatMoney(state.amount)}
        </div>
        <div className="mt-0.5 truncate text-sm text-zinc-600 dark:text-zinc-400">
          {state.status === 'credit'
            ? 'Credit on your account'
            : state.headline}
        </div>
      </div>
      <ChevronRightIcon className="h-5 w-5 shrink-0 text-zinc-400" />
    </Link>
  );
};

export default AccountBalanceCard;
